import { put, take, fork, takeLatest, call, select } from 'redux-saga/effects'
import _ from 'lodash'
import { Action } from 'redux'
import { handleRequest } from './requests'
import { STATE_KEYS } from '../state/requests'
import {
    CampaignTemplatesRequest,
    CampaignTemplatesResponse,
    getCampaignTemplates,
    SaveTemplateFiltersRequest,
    saveTemplateFiltersAndCreateCampaign,
    TemplateDetailRequest,
    TemplateDetail,
    getTemplateDetail,
    getTemplateData,
    TemplateDataRequest,
    TemplateData,
    switchTemplate,
    SwitchTemplateRequest,
    SwitchTemplateResponse,
    getCampaignInfo,
    CampaignInfoRequest,
    CampaignInfo,
    getCampaignSchedule,
    CampaignScheduleRequest,
    CampaignSchedule,
    CampaignTaskElementsRequest,
    CampaignTaskElements,
    getCampaignTaskElements,
    UpdateCampaignInfo,
    updateCampaignInfo,
    UpdateCampaignSchedule,
    updateCampaignSchedule,
    UpdateCampaignTaskElementsRequest,
    updateCampaignTaskElements,
    submitCampaignTemplate,
    getPaymentSummary,
    TemplateRequest,
    TemplatePaymentSummaryData,
    UpdatePaymentSummaryPayload,
    updatePaymentSummary,
    UpdateTemplateDataRequest,
    updateTemplateData,
    sendToEdit,
    requestPayment,
    sendToReview,
    publishTemplate,
    unpublishGuidedTemplate,
    useGuidedTemplate,
    duplicateGuidedTemplate,
    deleteCampaignTemplateData,
    getGuidedTemplateList,
    getRecommendedGuidedTemplateList,
    exportTemplate,
    GetPaymentSummaryPayload,
    publishGuidedCampaignTemplate,
    DuplicateGuidedTemplateRequest,
    getBrandsCountries,
} from '../services/api/campaignTemplates'
import {
    CampaignTemplatesActions,
    saveAllTemplates,
    saveFilteredTemplates,
    TemplateQueryType,
    FilterParameters,
    CreateCampaignByTemplatePayload,
    saveTemplateDetail,
    saveTemplateData,
    clearTemplateFilter,
    SwitchTemplatePayload,
    saveCampaignInfo,
    saveCampaignSchedule,
    saveCampaignTaskElements,
    updateTemplateUuid,
    saveTemplatePaymentSummary,
    PayloadTemplateCallback,
    PayloadExportTemplate,
    PublishGuidedTemplatePayload,
    saveGuidedTemplateList,
    GuidedTemplatePublishPayload,
    UseGuidedTemplatePayload,
    DuplicateGuidedTemplatePayload,
    updateGuidedTemplatePublishedStatus,
    updateGuidedTemplateData,
    RequestGuidedTemplateListPayload,
    DeleteCampaignTemplateDataPayload,
    removeGuidedTemplateData,
    saveRecommendedGuidedTemplateList,
    saveBrandsCountries,
    SaveBrandsCountriesPayload,
    getCampaignSchedule as requestCampaignSchedule,
} from '../state/campaignTemplates'
import { PLATFORM_CONTENT_TYPE_ENUM } from '../services/api/constants'
import { getProfileDetail } from 'state/profile'
import { resetTemplateFormError, saveTemplateFormError } from 'state/error/campaignTemplates'
import { showSnackBar } from 'services/toast'
import i18n from 'config/i18n'
import { ERROR_CODES } from 'state/error'
import { createGetRequestPagination, selectCampaign } from 'state'
import { saveCampaign, getCampaign } from 'state/campaigns'

// todo: support paginations if design support
export function* handleGetCampaignTemplates(filter: FilterParameters, type: TemplateQueryType): any {
    const filterParameters = _.omitBy(filter, _.isNull)
    yield handleRequest<CampaignTemplatesRequest, CampaignTemplatesResponse>({
        stateKey: STATE_KEYS.GetCampaignTemplates,
        payload: filterParameters,
        ApiHandler: getCampaignTemplates,
        *onSuccess(response) {
            if (type === 'all') {
                yield put(
                    saveAllTemplates({
                        data: response.data !== undefined ? response.data.data.results : undefined,
                    }),
                )
            } else {
                yield put(
                    saveFilteredTemplates({
                        data: response.data !== undefined ? response.data.data.results : undefined,
                    }),
                )
            }
        },
    })
}

export function* watchCampaignTemplatesRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.CAMPAIGN_TEMPLATES_REQUEST)
        const { filter, type } = action.payload
        yield fork(handleGetCampaignTemplates, filter, type)
    }
}

export function* handleCreateCampaignByTemplate(payload: CreateCampaignByTemplatePayload): any {
    const { templateFilter, onSuccessCallback, onFailCallback } = payload
    const platform_content_types = templateFilter.platform_content_types?.map(type => {
        if (type.indexOf(':') !== -1) {
            const splittedFilter = type.split(':')
            const isUGC = splittedFilter.indexOf('ugc') !== -1
            let isWithImage = null
            let isWithApproval = null

            if (splittedFilter.indexOf('image') !== -1 || splittedFilter.indexOf('non_image') !== -1) {
                isWithImage = splittedFilter.indexOf('image') !== -1
            }
            if (splittedFilter.indexOf('approval') !== -1 || splittedFilter.indexOf('non_approval') !== -1) {
                isWithApproval = splittedFilter.indexOf('approval') !== -1
            }
            return {
                platform_content_type: splittedFilter[0] as PLATFORM_CONTENT_TYPE_ENUM,
                create_content_required: isUGC,
                image_for_review_text_required: isWithImage,
                approval_for_review_text_required: isWithApproval,
            }
        } else {
            return {
                platform_content_type: type as PLATFORM_CONTENT_TYPE_ENUM,
                create_content_required: null,
                image_for_review_text_required: null,
                approval_for_review_text_required: null,
            }
        }
    })
    const requestPayload = {
        template: {
            uuid: templateFilter.templateUuid,
        },
        brand: {
            uuid: templateFilter.brandUuid,
        },
        objective: templateFilter.objective,
        get_product_required: templateFilter.get_product_required,
        platform_content_types: platform_content_types,
        title: payload.title,
        description: payload.description,
        filter_options: payload.filter_options,
        is_guided_flow: payload.is_guided_flow,
        // average_product_price: 0, // todo: confirm with Darwin about it. it's should be optional input
    }
    yield handleRequest<SaveTemplateFiltersRequest, any>({
        stateKey: STATE_KEYS.CreateCampaignByTemplate,
        payload: requestPayload,
        ApiHandler: saveTemplateFiltersAndCreateCampaign,
        *onSuccess(response) {
            yield put(clearTemplateFilter()) // clear templateFilter for redirect to template detail page
            yield put(saveTemplateData({ data: response.data !== undefined ? response.data.data : undefined }))
            yield onSuccessCallback(response.data?.data.uuid, response.data?.data.campaign.uuid)
            yield put(getProfileDetail())
        },
        *onError() {
            yield onFailCallback()
        },
    })
}

export function* watchCreateCampaignByTemplateRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.CREATE_CAMPAIGN_BY_TEMPLATE)
        yield fork(handleCreateCampaignByTemplate, action.payload)
    }
}

export function* handleUpdateTemplateData(payload: UpdateTemplateDataRequest): any {
    const { showSuccessToast, onSuccess, onError, ...rest } = payload
    yield handleRequest<UpdateTemplateDataRequest, any>({
        stateKey: STATE_KEYS.UpdateTemplateData,
        payload: rest,
        ApiHandler: updateTemplateData,
        *onSuccess(response) {
            const data = response.data?.data
            if (data?.campaign?.uuid) {
                yield put(getCampaign({ uuid: data.campaign.uuid }))
            }
            if (data) {
                yield put(
                    saveTemplateData({
                        data,
                    }),
                )
                yield put(updateGuidedTemplateData({ data }))
            }
            onSuccess?.()
            if (showSuccessToast) {
                yield call(showSnackBar, {
                    title: i18n.t('successMessage.saveChanges'),
                    type: 'positive',
                })
            }
        },
        *onError() {
            onError?.()
        },
    })
}

export function* watchUpdateTemplateDataRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.UPDATE_TEMPLATE_DATA)
        yield fork(handleUpdateTemplateData, action.payload)
    }
}

export function* handleGetTemplateDetail(payload: TemplateDetailRequest): any {
    yield handleRequest<TemplateDetailRequest, TemplateDetail>({
        stateKey: STATE_KEYS.GetTemplateDetail,
        payload: payload,
        ApiHandler: getTemplateDetail,
        *onSuccess(response) {
            yield put(
                saveTemplateDetail({
                    data: response.data !== undefined ? response.data.data : undefined,
                }),
            )
        },
    })
}

export function* watchTemplateDetailRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.TEMPLATE_DETAIL_REQUEST)
        yield fork(handleGetTemplateDetail, action.payload)
    }
}

export function* handleGetTemplateData(payload: TemplateDataRequest): any {
    yield handleRequest<TemplateDataRequest, TemplateData>({
        stateKey: STATE_KEYS.GetTemplateData,
        payload: payload,
        ApiHandler: getTemplateData,
        *onSuccess(response) {
            yield put(saveTemplateData({ data: response.data !== undefined ? response.data.data : undefined }))
        },
    })
}

export function* watchTemplateFiltersRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.TEMPLATE_DATA_REQUEST)
        yield fork(handleGetTemplateData, action.payload)
    }
}

export function* handleSwitchTemplate(payload: SwitchTemplatePayload): any {
    const { templateDataUuid, templateUuid } = payload
    yield handleRequest<SwitchTemplateRequest, SwitchTemplateResponse>({
        stateKey: STATE_KEYS.SwitchTemplate,
        payload: { templateDataUuid: templateDataUuid, templateUuid: templateUuid },
        ApiHandler: switchTemplate,
        *onSuccess(response) {
            if (response.data) yield put(updateTemplateUuid({ templateUuid: response.data.data.template.uuid }))
            yield payload.onSuccessCallback(payload.templateDataUuid, payload.campaignUuid)
        },
    })
}

export function* watchSwitchTemplateFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.SWITCH_TEMPLATE)
        yield fork(handleSwitchTemplate, action.payload)
    }
}

export function* handleGetCampaignInfo(payload: CampaignInfoRequest): any {
    yield handleRequest<CampaignInfoRequest, CampaignInfo>({
        stateKey: STATE_KEYS.GetCampaignInfo,
        payload: payload,
        ApiHandler: getCampaignInfo,
        *onSuccess(response) {
            yield put(
                saveCampaignInfo({
                    data: response.data !== undefined ? response.data.data : undefined,
                }),
            )
        },
    })
}

export function* watchCampaignInfoRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.CAMPAIGN_INFO_REQUEST)
        yield fork(handleGetCampaignInfo, action.payload)
    }
}

export function* handleUpdateCampaignInfo(payload: UpdateCampaignInfo): any {
    const { setNavigationDisable, showSuccessToast, ...restPayload } = payload
    yield handleRequest<UpdateCampaignInfo, CampaignInfo>({
        stateKey: STATE_KEYS.UpdateCampaignInfo,
        payload: restPayload,
        ApiHandler: updateCampaignInfo,
        *onSuccess(response) {
            yield put(
                saveCampaignInfo({
                    data: response.data !== undefined ? response.data.data : undefined,
                }),
            )
            if (restPayload?.registration_enable && restPayload?.templateDataUuid) {
                yield put(
                    requestCampaignSchedule({
                        templateDataUuid: restPayload.templateDataUuid,
                    }),
                )
            }
            yield setNavigationDisable?.(false)
            if (showSuccessToast) {
                yield call(showSnackBar, {
                    title: i18n.t('successMessage.saveChanges'),
                    type: 'positive',
                })
            }
        },
        *onError() {
            yield setNavigationDisable?.(false)
        },
    })
}

interface UpdateCampaignInfoAction extends Action {
    type: typeof CampaignTemplatesActions.UPDATE_CAMPAIGN_INFO
    payload: UpdateCampaignInfo
}

export function* watchUpdateCampaignInfoFlow(): any {
    yield takeLatest(CampaignTemplatesActions.UPDATE_CAMPAIGN_INFO, function* (action: UpdateCampaignInfoAction) {
        yield fork(handleUpdateCampaignInfo, action.payload)
    })
}

export function* handleGetCampaignSchedule(payload: CampaignScheduleRequest): any {
    yield handleRequest<CampaignScheduleRequest, CampaignSchedule>({
        stateKey: STATE_KEYS.GetCampaignSchedule,
        payload: payload,
        ApiHandler: getCampaignSchedule,
        *onSuccess(response) {
            yield put(
                saveCampaignSchedule({
                    data: response.data !== undefined ? response.data.data : undefined,
                }),
            )
        },
    })
}

export function* watchCampaignScheduleRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.CAMPAIGN_SCHEDULE_REQUEST)
        yield fork(handleGetCampaignSchedule, action.payload)
    }
}

export function* handleUpdateCampaignSchedule(payload: UpdateCampaignSchedule): any {
    const { setNavigationDisable, showSuccessToast, skipLoading, ...restPayload } = payload
    yield handleRequest<UpdateCampaignSchedule, CampaignSchedule>({
        stateKey: skipLoading ? STATE_KEYS.SkipLoading : STATE_KEYS.UpdateCampaignSchedule,
        payload: restPayload,
        ApiHandler: updateCampaignSchedule,
        *onSuccess(response) {
            yield put(
                saveCampaignSchedule({
                    data: response.data !== undefined ? response.data.data : undefined,
                }),
            )

            yield setNavigationDisable?.(false)
            if (showSuccessToast) {
                yield call(showSnackBar, {
                    title: i18n.t('successMessage.saveChanges'),
                    type: 'positive',
                })
            }
        },
        *onError(response) {
            yield setNavigationDisable?.(false)
            // TODO temporary error message until we fix the backend logic
            if (response?.general_errors?.reasons?.includes(ERROR_CODES.SCHEDULE_CHANGE_BACKWARD_NOT_ALLOWED)) {
                yield call(showSnackBar, {
                    title: i18n.t('errorMessage.general.unknownError'),
                    type: 'error',
                })
            }
        },
    })
}

export function* watchUpdateCampaignScheduleFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.UPDATE_CAMPAIGN_SCHEDULE)
        yield fork(handleUpdateCampaignSchedule, action.payload)
    }
}

export function* handleGetCampaignTaskElements(payload: CampaignTaskElementsRequest): any {
    yield handleRequest<CampaignTaskElementsRequest, CampaignTaskElements[]>({
        stateKey: STATE_KEYS.GetCampaignTaskElements,
        payload: payload,
        ApiHandler: getCampaignTaskElements,
        *onSuccess(response) {
            if (response.data?.data) {
                yield put(saveCampaignTaskElements(response.data?.data))
            }
        },
    })
}

export function* watchCampaignTaskElementsRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.CAMPAIGN_TASK_ELEMENTS_REQUEST)
        yield fork(handleGetCampaignTaskElements, action.payload)
    }
}

export function* handleUpdateCampaignTaskElements(payload: UpdateCampaignTaskElementsRequest): any {
    const { setNavigationDisable, showSuccessToast, ...restPayload } = payload
    yield handleRequest<UpdateCampaignTaskElementsRequest, CampaignTaskElements[]>({
        stateKey: STATE_KEYS.UpdateCampaignTaskElements,
        payload: restPayload,
        ApiHandler: updateCampaignTaskElements,
        *onSuccess() {
            yield setNavigationDisable?.(false)
            if (showSuccessToast) {
                yield call(showSnackBar, {
                    title: i18n.t('successMessage.saveChanges'),
                    type: 'positive',
                })
            }
        },
        *onError() {
            yield setNavigationDisable?.(false)
        },
    })
}

export function* watchCampaignTaskElementsUpdateFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.UPDATE_CAMPAIGN_TASK_ELEMENTS)
        yield fork(handleUpdateCampaignTaskElements, action.payload)
    }
}

export function* handleSubmitCampaignTemplate(payload: PayloadTemplateCallback): any {
    const { onSuccessCallback, onErrorCallback, ...restPayload } = payload
    yield handleRequest<TemplateDetailRequest, object>({
        stateKey: STATE_KEYS.SubmitCampaignTemplate,
        payload: restPayload,
        ApiHandler: submitCampaignTemplate,
        *onSuccess() {
            yield onSuccessCallback?.()
        },
        *onError(response) {
            yield onErrorCallback?.()
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors

                yield put(resetTemplateFormError())
                yield put(saveTemplateFormError(errors))
            }
        },
    })
}

export function* watchCampaignTemplateSubmitFlow(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.SUBMIT_CAMPAIGN)
        yield fork(handleSubmitCampaignTemplate, action.payload)
    }
}

export function* handleGetCampaignTemplatePaymentSummary(payload: GetPaymentSummaryPayload): any {
    yield handleRequest<GetPaymentSummaryPayload, TemplatePaymentSummaryData>({
        stateKey: STATE_KEYS.GetPaymentSummary,
        payload,
        ApiHandler: getPaymentSummary,
        *onSuccess(response) {
            if (response.data?.data) {
                yield put(saveTemplatePaymentSummary(response.data?.data))
            }
        },
    })
}

export function* watchCampaignTemplatePaymentSummary(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.GET_PAYMENT_SUMMARY)
        yield fork(handleGetCampaignTemplatePaymentSummary, action.payload)
    }
}

export function* handleUpdateCampaignTemplatePaymentSummary(payload: UpdatePaymentSummaryPayload): any {
    yield handleRequest<UpdatePaymentSummaryPayload, TemplatePaymentSummaryData>({
        stateKey: STATE_KEYS.UpdatePaymentSummary,
        payload,
        ApiHandler: updatePaymentSummary,
        *onSuccess(response) {
            if (response.data?.data) {
                yield put(saveTemplatePaymentSummary(response.data?.data))
            }
        },
    })
}

export function* watchUpdateCampaignTemplatePaymentSummary(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.UPDATE_PAYMENT_SUMMARY)
        yield fork(handleUpdateCampaignTemplatePaymentSummary, action.payload)
    }
}

export function* handleSendToEdit(payload: PayloadTemplateCallback): any {
    const { onErrorCallback, onSuccessCallback, ...restPayload } = payload
    yield handleRequest<TemplateRequest, object>({
        stateKey: STATE_KEYS.TemplateSendToEdit,
        payload: restPayload,
        ApiHandler: sendToEdit,
        *onSuccess() {
            yield onSuccessCallback?.()
        },
        *onError(response) {
            let title = i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext')
            yield onErrorCallback?.()
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                title = i18n.t('errorMessage.validation.enterDetailsRedSubtext')

                yield put(resetTemplateFormError())
                yield put(saveTemplateFormError(errors))
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.UPDATE_NOT_ALLOWED)) {
                title = i18n.t('errorMessage.businessLogic.hasMoveToEditStates.subtext')
            }

            showSnackBar({
                type: 'error',
                title,
            })
        },
    })
}

export function* watchSendToEdit(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.SEND_TO_EDIT)
        yield fork(handleSendToEdit, action.payload)
    }
}

export function* handleRequestPayment(payload: PayloadTemplateCallback): any {
    const { onErrorCallback, onSuccessCallback, ...restPayload } = payload
    yield handleRequest<TemplateRequest, object>({
        stateKey: STATE_KEYS.TemplateRequestPayment,
        payload: restPayload,
        ApiHandler: requestPayment,
        *onSuccess() {
            yield onSuccessCallback?.()
        },
        *onError(response) {
            let message = i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext')
            yield onErrorCallback?.()
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                message = i18n.t('errorMessage.validation.enterDetailsRedSubtext')

                yield put(resetTemplateFormError())
                yield put(saveTemplateFormError(errors))
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.UPDATE_NOT_ALLOWED)) {
                message = i18n.t('errorMessage.hasMovedCampaignStatus', {
                    campaignStatus: i18n.t('statusLabel.waitingForPayment'),
                })
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.PERMISSION_DENIED)) {
                message = i18n.t('errorMessage.campaignEditor.toast.cantTakeActionTitle')
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.CAMPAIGN_NO_PAY_PERMISSION_USER)) {
                message = i18n.t('errorMessage.noPayCampaignPermissionInTeam')
            }
            showSnackBar({
                type: 'error',
                title: message,
            })
        },
    })
}

export function* watchRequestPayment(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.REQUEST_PAYMENT)
        yield fork(handleRequestPayment, action.payload)
    }
}

export function* handleSendToReview(payload: PayloadTemplateCallback): any {
    const { onErrorCallback, onSuccessCallback, ...restPayload } = payload
    yield handleRequest<TemplateRequest, object>({
        stateKey: STATE_KEYS.TemplateSendToReview,
        payload: restPayload,
        ApiHandler: sendToReview,
        *onSuccess() {
            yield onSuccessCallback?.()
        },
        *onError(response) {
            let message = i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext')
            yield onErrorCallback?.()
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                message = i18n.t('errorMessage.validation.enterDetailsRedSubtext')

                yield put(resetTemplateFormError())
                yield put(saveTemplateFormError(errors))
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.UPDATE_NOT_ALLOWED)) {
                message = i18n.t('errorMessage.hasMovedCampaignStatus', {
                    campaignStatus: i18n.t('statusLabel.forReviewAllCapital'),
                })
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.PERMISSION_DENIED)) {
                message = i18n.t('errorMessage.campaignEditor.toast.cantTakeActionTitle')
            }
            showSnackBar({
                type: 'error',
                title: message,
            })
        },
    })
}

export function* watchSendToReview(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.SEND_TO_REVIEW)
        yield fork(handleSendToReview, action.payload)
    }
}

export function* handlePublishCampaignTemplate(payload: PayloadTemplateCallback): any {
    const { onErrorCallback, onSuccessCallback, ...restPayload } = payload
    yield handleRequest<TemplateRequest, object>({
        stateKey: STATE_KEYS.TemplatePublish,
        payload: restPayload,
        ApiHandler: publishTemplate,
        *onSuccess() {
            yield onSuccessCallback?.()
        },
        *onError(response) {
            let message = i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext')
            yield onErrorCallback?.()
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                message = i18n.t('errorMessage.validation.enterDetailsRedSubtext')

                yield put(resetTemplateFormError())
                yield put(saveTemplateFormError(errors))
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.UPDATE_NOT_ALLOWED)) {
                message = i18n.t('errorMessage.campaignEditor.toast.campaignMovedToLiveSubtext')
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.PERMISSION_DENIED)) {
                message = i18n.t('errorMessage.campaignEditor.toast.cantTakeActionTitle')
            }

            showSnackBar({
                type: 'error',
                title: message,
            })
        },
    })
}

export function* watchPublishCampaignTemplate(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.PUBLISH_TEMPLATE)
        yield fork(handlePublishCampaignTemplate, action.payload)
    }
}

export function* handleUnpublishGuidedTemplate(payload: GuidedTemplatePublishPayload): any {
    const { onErrorCallback, onSuccessCallback, ...restPayload } = payload
    yield handleRequest<TemplateRequest, undefined>({
        stateKey: STATE_KEYS.UnpublishGuidedTemplate,
        payload: restPayload,
        ApiHandler: unpublishGuidedTemplate,
        *onSuccess() {
            yield put(
                updateGuidedTemplatePublishedStatus({ templateUuid: restPayload.templateUuid, isPublished: false }),
            )
            yield onSuccessCallback?.()
        },
        *onError(response) {
            yield onErrorCallback?.()
            if (onErrorCallback) return
            let message = i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext')
            if (response?.general_errors?.reasons?.includes(ERROR_CODES.PERMISSION_DENIED)) {
                message = i18n.t('errorMessage.campaignEditor.toast.cantTakeActionTitle')
            }
            showSnackBar({
                type: 'error',
                title: message,
            })
        },
    })
}

export function* watchUnpublishGuidedTemplate(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.UNPUBLISH_GUIDED_TEMPLATE)
        yield fork(handleUnpublishGuidedTemplate, action.payload)
    }
}

export function* handleUseGuidedTemplate(payload: UseGuidedTemplatePayload): any {
    const { onErrorCallback, onSuccessCallback, ...restPayload } = payload
    yield handleRequest<TemplateDataRequest, TemplateData>({
        stateKey: STATE_KEYS.UseGuidedTemplate,
        payload: { templateDataUuid: restPayload.templateDataUuid },
        ApiHandler: useGuidedTemplate,
        *onSuccess(response) {
            const templateDataUuid = response.data?.data.uuid
            const campaignUuid = response.data?.data.campaign.uuid
            if (templateDataUuid && campaignUuid) {
                yield onSuccessCallback?.(templateDataUuid, campaignUuid)
            }
        },
        *onError(response) {
            yield onErrorCallback?.()
            if (onErrorCallback) return
            let message = i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext')
            if (response?.general_errors?.reasons?.includes(ERROR_CODES.PERMISSION_DENIED)) {
                message = i18n.t('errorMessage.campaignEditor.toast.cantTakeActionTitle')
            }
            showSnackBar({
                type: 'error',
                title: message,
            })
        },
    })
}

export function* watchUseGuidedTemplate(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.USE_GUIDED_TEMPLATE)
        yield fork(handleUseGuidedTemplate, action.payload)
    }
}

export function* handleDuplicateGuidedTemplate(payload: DuplicateGuidedTemplatePayload): any {
    const { onErrorCallback, onSuccessCallback, ...restPayload } = payload
    yield handleRequest<DuplicateGuidedTemplateRequest, TemplateData>({
        stateKey: STATE_KEYS.DuplicateGuidedTemplate,
        payload: restPayload,
        ApiHandler: duplicateGuidedTemplate,
        *onSuccess(response) {
            const data = response.data?.data

            if (data?.uuid && data.campaign.uuid) {
                yield onSuccessCallback?.(data.uuid, data.campaign.uuid)
            }
        },
        *onError(response) {
            yield onErrorCallback?.()
            if (onErrorCallback) return
            let message = i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext')
            if (response?.general_errors?.reasons?.includes(ERROR_CODES.PERMISSION_DENIED)) {
                message = i18n.t('errorMessage.campaignEditor.toast.cantTakeActionTitle')
            }
            showSnackBar({
                type: 'error',
                title: message,
            })
        },
    })
}

export function* watchDuplicateGuidedTemplate(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.DUPLICATE_GUIDED_TEMPLATE)
        yield fork(handleDuplicateGuidedTemplate, action.payload)
    }
}

export function* handleDeleteCampaignTemplateData(payload: DeleteCampaignTemplateDataPayload): any {
    const { onErrorCallback, onSuccessCallback, ...restPayload } = payload
    yield handleRequest<TemplateDataRequest, undefined>({
        stateKey: STATE_KEYS.DeleteCampaignTemplateData,
        payload: {
            templateDataUuid: restPayload.templateDataUuid,
        },
        ApiHandler: deleteCampaignTemplateData,
        *onSuccess() {
            yield put(removeGuidedTemplateData({ templateDataUuid: restPayload.templateDataUuid }))
            yield onSuccessCallback?.()
        },
        *onError(response) {
            yield onErrorCallback?.()
            if (onErrorCallback) return
            let message = i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext')
            if (response?.general_errors?.reasons?.includes(ERROR_CODES.PERMISSION_DENIED)) {
                message = i18n.t('errorMessage.campaignEditor.toast.cantTakeActionTitle')
            }
            showSnackBar({
                type: 'error',
                title: message,
            })
        },
    })
}

export function* watchDeleteCampaignTemplateData(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.DELETE_CAMPAIGN_TEMPLATE_DATA)
        yield fork(handleDeleteCampaignTemplateData, action.payload)
    }
}

export function* handleExportTemplate(payload: PayloadExportTemplate): any {
    const { onErrorCallback, onSuccessCallback, campaignUuid, ...restPayload } = payload
    const campaign = yield select(selectCampaign(campaignUuid))
    yield handleRequest<TemplateRequest, object>({
        stateKey: STATE_KEYS.ExportTemplate,
        payload: restPayload,
        ApiHandler: exportTemplate,
        *onSuccess() {
            if (campaign) {
                yield put(saveCampaign({ data: { ...campaign, in_template_flow: false } }))
            }
            yield onSuccessCallback?.()
        },
        *onError(response) {
            let title = i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext')
            yield onErrorCallback?.()
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                title = i18n.t('errorMessage.validation.enterDetailsRedSubtext')

                yield put(resetTemplateFormError())
                yield put(saveTemplateFormError(errors))
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.UPDATE_NOT_ALLOWED)) {
                title = i18n.t('errorMessage.businessLogic.hasMoveToCampaignEditor.subtext')
            } else if (response?.general_errors?.reasons)
                showSnackBar({
                    type: 'error',
                    title,
                })
        },
    })
}

export function* watchExportTemplate(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.EXPORT_TEMPLATE)
        yield fork(handleExportTemplate, action.payload)
    }
}

export function* handlePublishGuidedCampaignTemplate(payload: PublishGuidedTemplatePayload): any {
    const { onSuccessCallback, ...restPayload } = payload
    yield handleRequest<TemplateRequest, object>({
        stateKey: STATE_KEYS.PublishGuidedCampaignTemplate,
        payload: restPayload,
        ApiHandler: publishGuidedCampaignTemplate,
        *onSuccess() {
            yield onSuccessCallback?.()
            yield call(showSnackBar, {
                type: 'positive',
                title: i18n.t('successAlert.guidedTemplate.published'),
            })
        },
        *onError(response) {
            yield call(showSnackBar, {
                type: 'error',
                title: i18n.t('errorAlert.guidedTemplate.publishFailed'),
            })
        },
    })
}

export function* watchPublishGuidedCampaignTemplate(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.PUBLISH_GUIDED_CAMPAIGN_TEMPLATE)
        yield fork(handlePublishGuidedCampaignTemplate, action.payload)
    }
}
export function* handleGetGuidedTemplateList(payload: RequestGuidedTemplateListPayload): any {
    const pagination = yield select(createGetRequestPagination(STATE_KEYS.GetGuidedTemplateList))
    const { hasMore, next } = pagination
    const { keywords, ...rest } = payload
    yield handleRequest<{ keywords: string; limit: number; ordering: string; url: string }, CampaignTemplatesResponse>({
        stateKey: STATE_KEYS.GetGuidedTemplateList,
        payload: {
            ...rest,
            keywords: keywords || '',
            limit: 18,
            ordering: keywords && String(keywords).length > 0 ? '' : '-created_at',
            url: hasMore ? next : '',
        },
        ApiHandler: getGuidedTemplateList,
        *onSuccess(response) {
            if (response.data?.data.results) {
                yield put(
                    saveGuidedTemplateList({
                        data: response.data?.data.results,
                    }),
                )
            }
        },
    })
}
export function* watchGetGuidedTemplateList(): any {
    while (true) {
        const action = yield take(CampaignTemplatesActions.GET_GUIDED_TEMPLATE_LIST)
        yield fork(handleGetGuidedTemplateList, action.payload)
    }
}

export function* handleGetRecommendedGuidedTemplateList(): any {
    yield handleRequest<undefined, TemplateData[]>({
        stateKey: STATE_KEYS.GetRecommendedGuidedTemplateList,
        payload: undefined,
        ApiHandler: getRecommendedGuidedTemplateList,
        *onSuccess(response) {
            if (response.data?.data) {
                yield put(
                    saveRecommendedGuidedTemplateList({
                        data: response.data?.data,
                    }),
                )
            }
        },
    })
}

export function* watchGetRecommendedGuidedTemplateList(): any {
    while (true) {
        yield take(CampaignTemplatesActions.GET_RECOMMENDED_GUIDED_TEMPLATE_LIST)
        yield fork(handleGetRecommendedGuidedTemplateList)
    }
}

export function* handleGetBrandsCountries(): any {
    yield handleRequest<SaveBrandsCountriesPayload, any>({
        stateKey: STATE_KEYS.GetBrandsCountries,
        payload: {},
        ApiHandler: getBrandsCountries,
        *onSuccess(response) {
            yield put(
                saveBrandsCountries({
                    countries: response?.data?.data,
                }),
            )
        },
    })
}

export function* watchGetBrandsCountries(): any {
    while (true) {
        yield take(CampaignTemplatesActions.BRANDS_COUNTRIES_REQUEST)
        yield fork(handleGetBrandsCountries)
    }
}
